import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { isEqual, isPast } from 'date-fns';
import { useEffect } from 'react';

import type { Sport } from '__generated__/globalTypes';
import { ConversionCreditStatus } from '__generated__/globalTypes';
import { useCurrentUserContext } from 'contexts/currentUser';
import type { CurrentUserProvider_sportConversionCredits } from 'contexts/currentUser/__generated__/queries.graphql';
import { currentUserConversionCredits } from 'contexts/currentUser/queries';
import { Sum, useMonetaryAmount } from 'hooks/useMonetaryAmount';
import type { MonetaryAmount } from 'lib/monetaryAmount';

import type {
  RefreshSportConversionCreditsQuery,
  RefreshSportConversionCreditsQueryVariables,
} from './__generated__/useConversionCredits.graphql';

export type ConversionCreditWithAmounts = Omit<
  NonNullable<
    CurrentUserProvider_sportConversionCredits['sportConversionCredits']
  >['nodes'][0],
  'maxDiscount' | 'totalDiscount'
> & {
  maxDiscount: MonetaryAmount;
  totalDiscount: MonetaryAmount;
  disabled: boolean;
};

const calculateCreditAmount = (
  amount: MonetaryAmount,
  conversionCredit: ConversionCreditWithAmounts
) => {
  return Math.min(
    conversionCredit.maxDiscount.eurCents,
    amount.eurCents * conversionCredit.percentageDiscount
  );
};

export const getBestConversionCredit = (
  amount: MonetaryAmount,
  conversionCredits: ConversionCreditWithAmounts[]
) => {
  return conversionCredits.reduce((bestConversionCredit, conversionCredit) => {
    if (
      calculateCreditAmount(amount, conversionCredit) >
      calculateCreditAmount(amount, bestConversionCredit)
    )
      return conversionCredit;
    return bestConversionCredit;
  }, conversionCredits[0]);
};

const allowedStatuses: ConversionCreditStatus[] = [
  ConversionCreditStatus.CLAIMED,
  ConversionCreditStatus.RECLAIMED,
  ConversionCreditStatus.CREATED,
  ConversionCreditStatus.USING,
];

const REFRESH_CONVERSION_CREDITS_QUERY = gql`
  query RefreshSportConversionCreditsQuery {
    currentUser {
      slug
      ...CurrentUserProvider_sportConversionCredits
    }
  }
  ${currentUserConversionCredits}
` as TypedDocumentNode<
  RefreshSportConversionCreditsQuery,
  RefreshSportConversionCreditsQueryVariables
>;

export const useConversionCredits = ({
  currentlyUsedConversionCredit,
  sport,
  forceRefetch,
}: {
  currentlyUsedConversionCredit?: ConversionCreditWithAmounts;
  sport?: Sport;
  forceRefetch?: boolean;
} = {}): {
  conversionCredits: ConversionCreditWithAmounts[] | undefined;
  groupedConversionCredits: Record<number, ConversionCreditWithAmounts[]>;
  loading: boolean;
} => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { currentUser, loading } = useCurrentUserContext();
  const [refetch] = useLazyQuery(REFRESH_CONVERSION_CREDITS_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (forceRefetch) refetch();
  }, [forceRefetch, refetch]);

  const conversionCredits = currentUser?.sportConversionCredits?.nodes || [];

  const filteredConversionCredit = conversionCredits.reduce<
    ConversionCreditWithAmounts[]
  >((acc, conversionCredit_) => {
    const conversionCredit = conversionCredit_;

    if (
      !allowedStatuses.includes(conversionCredit.status) ||
      isPast(conversionCredit.endDate) ||
      (sport && conversionCredit.sport && sport !== conversionCredit.sport)
    ) {
      return acc;
    }
    acc.push({
      ...conversionCredit,
      disabled:
        conversionCredit.status === ConversionCreditStatus.USING &&
        conversionCredit.id !== currentlyUsedConversionCredit?.id,
      maxDiscount: toMonetaryAmount(conversionCredit.maxDiscount),
      totalDiscount: toMonetaryAmount(conversionCredit.totalDiscount),
    });
    return acc;
  }, []);

  // Merge credits with the same endDate/percentageDiscount/sport/status
  const mergedConversionCredits = filteredConversionCredit.reduce(
    (acc, credit) => {
      const existingCredit = acc.find(
        c =>
          isEqual(c.endDate, credit.endDate) &&
          c.percentageDiscount === credit.percentageDiscount &&
          c.sport === credit.sport &&
          !credit.singleUse &&
          [
            ConversionCreditStatus.CLAIMED,
            ConversionCreditStatus.RECLAIMED,
          ].includesValue(c.status) &&
          [
            ConversionCreditStatus.CLAIMED,
            ConversionCreditStatus.RECLAIMED,
          ].includesValue(credit.status)
      );
      if (existingCredit) {
        existingCredit.maxDiscount = Sum([
          existingCredit.maxDiscount,
          credit.maxDiscount,
        ]);
        existingCredit.totalDiscount = Sum([
          existingCredit.totalDiscount,
          credit.totalDiscount,
        ]);
        return acc;
      }
      return [...acc, { ...credit }];
    },
    [] as ConversionCreditWithAmounts[]
  );

  // Group credits by percentageDiscount
  const groupedConversionCredits = mergedConversionCredits.reduce<
    Record<number, typeof filteredConversionCredit>
  >((acc, cc) => {
    acc[cc.percentageDiscount] = [...(acc[cc.percentageDiscount] || []), cc];
    return acc;
  }, {});

  return {
    groupedConversionCredits,
    conversionCredits: filteredConversionCredit,
    loading,
  };
};
